// Documents: attachments and the Library.
import { postForm, request } from './api';

export type ViewerKind = 'pdf' | 'image' | 'download';
export type ExpiryStatus = 'none' | 'ok' | 'soon' | 'expired';
export type OwnerKind = 'job' | 'resource' | 'library' | 'post' | 'quote' | 'timesheet';

/**
 * The folders a resource's documents sit in. Null on every other owner, and on
 * everything uploaded before folders existed — which reads as Miscellaneous.
 *
 * Which of these a given resource may use depends on its kind: a person's four
 * are in `lib/personSections.ts` (round 27), a machine's seven in
 * `lib/attachmentFolders.ts` (round 31), and the server refuses the rest.
 */
export type AttachmentCategory =
  | 'competencies'
  | 'inductions'
  | 'permits_registrations'
  | 'manuals'
  | 'risk_assessments'
  | 'service_invoices'
  | 'warranty'
  | 'photos'
  | 'miscellaneous';

export interface Attachment {
  id: string;
  title: string;
  description: string | null;
  contentType: string;
  sizeBytes: number;
  originalFilename: string | null;
  viewer: ViewerKind;
  expiresOn: string | null;
  expiry: ExpiryStatus;
  category: AttachmentCategory | null;
  /** The competency or induction this is evidence for, if any. */
  personRecordId: string | null;
  /** The service or repair this is the invoice for, if any. */
  serviceRecordId: string | null;
  /** The insurance this is the certificate of currency for, if any (round 41). */
  insuranceRecordId: string | null;
  isSitePhoto: boolean;
  /** A job's purchase order (round 45). */
  isPurchaseOrder?: boolean;
  uploadedById: string | null;
  uploadedAt: string;
  deletedAt: string | null;
  ownerKind: OwnerKind;
  ownerId: string;
}

/** A recycle-bin row: the attachment plus where it came from and who deleted it. */
export interface DeletedAttachment extends Attachment {
  /** The job's number and title, the resource's name, the library section, or
      the first line of the post. */
  ownerName: string;
  /** Null when the deleter's account has since been removed. */
  deletedByName: string | null;
}

export interface LibrarySection {
  id: string;
  name: string;
  sortOrder: number;
  documents: Attachment[];
}

/**
 * Where to point an <img> or <object> at.
 *
 * The browser fetches it with the session cookie attached, and the server
 * checks permission on every request — these are private site documents, and a
 * URL that worked without a session would be a URL anyone could forward on.
 */
export function contentUrl(id: string, options: { download?: boolean } = {}): string {
  return `/api/attachments/${id}/content${options.download ? '?download=true' : ''}`;
}

/** One document, filed. The transport (and its progress bar) is `postForm`. */
export function uploadAttachment(
  input: {
    kind: OwnerKind;
    ownerId: string;
    file: File;
    title?: string;
    description?: string;
    expiresOn?: string;
    category?: AttachmentCategory;
    /** Files it against a competency or induction — the folder follows. */
    recordId?: string;
    /** Files it against a service or repair — the folder follows. */
    serviceRecordId?: string;
    /** Files it against a subcontractor's insurance — no folder; the flat list. */
    insuranceRecordId?: string;
    /**
     * A manager's image lands in the job's Site photos group rather than the
     * document list (round 37). Field workers never need it — their uploads
     * are site photos by construction.
     */
    sitePhoto?: boolean;
    /** A job's purchase order file (round 45); replaces the last one. */
    purchaseOrder?: boolean;
  },
  onProgress?: (fraction: number) => void
): Promise<{ attachment: Attachment }> {
  const form = new FormData();
  form.append('kind', input.kind);
  form.append('ownerId', input.ownerId);
  if (input.title) form.append('title', input.title);
  if (input.description) form.append('description', input.description);
  if (input.expiresOn) form.append('expiresOn', input.expiresOn);
  if (input.category) form.append('category', input.category);
  if (input.recordId) form.append('recordId', input.recordId);
  if (input.serviceRecordId) form.append('serviceRecordId', input.serviceRecordId);
  if (input.insuranceRecordId) form.append('insuranceRecordId', input.insuranceRecordId);
  if (input.sitePhoto) form.append('sitePhoto', 'true');
  if (input.purchaseOrder) form.append('purchaseOrder', 'true');
  form.append('file', input.file, input.file.name);

  return postForm<{ attachment: Attachment }>('/attachments', form, onProgress);
}

export const documents = {
  list: (kind: OwnerKind, ownerId: string) =>
    request<{ attachments: Attachment[] }>(
      `/attachments?kind=${kind}&ownerId=${encodeURIComponent(ownerId)}`
    ),

  update: (
    id: string,
    body: {
      title?: string;
      description?: string | null;
      expiresOn?: string | null;
      librarySectionId?: string;
      category?: AttachmentCategory | null;
      /** Null unlinks: the file stays where it is, loose, in its folder. */
      recordId?: string | null;
      serviceRecordId?: string | null;
      insuranceRecordId?: string | null;
    }
  ) => request<{ attachment: Attachment }>(`/attachments/${id}`, { method: 'PATCH', body }),

  remove: (id: string) => request<void>(`/attachments/${id}`, { method: 'DELETE' }),

  restore: (id: string) =>
    request<{ attachment: Attachment }>(`/attachments/${id}/restore`, { method: 'POST' }),

  deleted: () => request<{ attachments: DeletedAttachment[] }>('/attachments/deleted'),

  expiring: () =>
    request<{ documents: (Attachment & { resourceName: string })[] }>('/attachments/expiring'),

  library: (search?: string) =>
    request<{ sections: LibrarySection[] }>(
      `/library${search ? `?search=${encodeURIComponent(search)}` : ''}`
    ),

  createSection: (name: string) =>
    request<{ section: { id: string; name: string; sortOrder: number } }>('/library/sections', {
      method: 'POST',
      body: { name },
    }),

  renameSection: (id: string, name: string) =>
    request<{ section: { id: string; name: string } }>(`/library/sections/${id}`, {
      method: 'PATCH',
      body: { name },
    }),

  deleteSection: (id: string) => request<void>(`/library/sections/${id}`, { method: 'DELETE' }),
};
